import { useCallback, useEffect, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { QRCodeSVG } from 'qrcode.react'

const PENDING = 3
const PAID = 1
const CANCELED = 2

function isImageUrl(value) {
  return /^(https?:|data:)/i.test(String(value ?? '').trim())
}

function qrImageSrc(payment, invoice) {
  const candidates = [
    payment?.qrUrl,
    payment?.qr_url,
    invoice?.emvQrImg,
    invoice?.emv_qr_img,
    invoice?.qr,
    invoice?.qrUrl,
    invoice?.qr_url,
  ]
  for (const value of candidates) {
    const text = String(value ?? '').trim()
    if (isImageUrl(text)) return text
  }
  return ''
}

function qrPayload(invoice) {
  const candidates = [invoice?.emvQr, invoice?.emv_qr, invoice?.qr]
  for (const value of candidates) {
    const text = String(value ?? '').trim()
    if (text && !isImageUrl(text)) return text
  }
  return ''
}

function paymentStatusIdOf(payment) {
  const id = Number(payment?.statusId ?? payment?.status_id)
  return Number.isFinite(id) ? id : PENDING
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value)
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

function statusLabel(statusId) {
  if (statusId === PAID) return 'Оплачено'
  if (statusId === CANCELED) return 'Оплата отменена'
  return 'Ожидает оплаты'
}

function dataUrlToBlob(dataUrl) {
  const [header, body] = String(dataUrl).split(',')
  const mime = /data:(.*?);/.exec(header)?.[1] || 'image/png'
  const binary = atob(body || '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

export default function OrderPayQrModal({
  open,
  payment,
  invoice,
  orderId,
  onClose,
  onPoll,
  onSnapshot,
}) {
  const captureRef = useRef(null)
  const [current, setCurrent] = useState(payment)
  const [busy, setBusy] = useState(false)
  const [shareError, setShareError] = useState(null)

  useEffect(() => {
    setCurrent(payment)
    setShareError(null)
  }, [payment])

  const statusId = paymentStatusIdOf(current)
  const paymentId = current?.id ?? current?._id ?? null

  useEffect(() => {
    if (!open || paymentId == null || statusId !== PENDING) return
    if (typeof onPoll !== 'function') return

    let stopped = false
    const tick = async () => {
      const next = await onPoll(paymentId)
      if (stopped || !next?.payment) return
      setCurrent((prev) => ({
        ...(prev || {}),
        ...next.payment,
        qrUrl: next.payment.qrUrl || next.payment.qr_url || prev?.qrUrl || prev?.qr_url || null,
      }))
      if (typeof onSnapshot === 'function') onSnapshot(next)
    }

    tick()
    const timer = setInterval(tick, 2000)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [open, paymentId, statusId, onPoll, onSnapshot])

  const captureBlob = useCallback(async () => {
    const imageSrc = qrImageSrc(current, invoice)
    if (imageSrc.startsWith('data:')) return dataUrlToBlob(imageSrc)
    if (imageSrc.startsWith('http')) {
      try {
        const response = await fetch(imageSrc)
        if (response.ok) return await response.blob()
      } catch {
        /* fall through to canvas */
      }
    }

    const element = captureRef.current
    if (!element) return null
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#161b22',
      logging: false,
    })
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png')
    })
  }, [current, invoice])

  const handleSave = useCallback(async () => {
    if (busy) return
    try {
      setBusy(true)
      setShareError(null)
      const blob = await captureBlob()
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = `payment-${orderId ?? paymentId ?? 'qr'}.png`
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy(false)
    }
  }, [busy, captureBlob, orderId, paymentId])

  const handleShare = useCallback(async () => {
    if (busy) return
    try {
      setBusy(true)
      setShareError(null)
      const blob = await captureBlob()
      if (!blob) return
      const fileName = `payment-${orderId ?? paymentId ?? 'qr'}.png`
      const file = new File([blob], fileName, { type: blob.type || 'image/png' })
      const title = orderId != null ? `Оплата заказа №${orderId}` : 'Оплата заказа'
      if (typeof navigator !== 'undefined' && navigator.share) {
        try {
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ title, files: [file] })
            return
          }
          await navigator.share({ title, text: title })
          return
        } catch (e) {
          if (e?.name === 'AbortError') return
        }
      }
      setShareError('Не удалось открыть меню «Поделиться». Сохраните изображение кнопкой «Сохранить».')
    } finally {
      setBusy(false)
    }
  }, [busy, captureBlob, orderId, paymentId])

  if (!open) return null

  const imageSrc = qrImageSrc(current, invoice)
  const payload = imageSrc ? '' : qrPayload(invoice)

  return (
    <div className="client-order-pay-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="client-order-pay-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Оплата"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="client-order-pay-header">
          <div className="client-order-pay-title">{formatMoney(current?.amount)} сом</div>
          <button type="button" className="client-order-pay-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div ref={captureRef} className="client-order-pay-capture">
          <div className={`client-order-pay-status client-order-pay-status--${statusId}`}>
            {statusLabel(statusId)}
          </div>
          {imageSrc ? (
            <img className="client-order-pay-qr" src={imageSrc} alt="QR оплаты" />
          ) : payload ? (
            <div className="client-order-pay-qrSvg">
              <QRCodeSVG value={payload} size={220} level="M" includeMargin={false} />
            </div>
          ) : (
            <div className="client-order-pay-missing">QR недоступен</div>
          )}
        </div>
        {shareError ? <div className="client-order-pay-error">{shareError}</div> : null}
        <div className="client-order-pay-actions">
          <button
            type="button"
            className="client-order-detail-btn client-order-detail-btn--primary"
            onClick={handleSave}
            disabled={busy}
          >
            Сохранить
          </button>
          <button
            type="button"
            className="client-order-detail-btn client-order-detail-btn--primary"
            onClick={handleShare}
            disabled={busy}
          >
            Поделиться
          </button>
        </div>
      </div>
    </div>
  )
}
